import React, { useEffect, useMemo } from 'react';
import {
	IItemForGetShortInfo,
	IGetDishesShort,
	IGetDishesShortBySubCategoryIdsArguments,
	ITeaserDishTransition,
} from 'types';
import { Rule } from 'antd/lib/form';
import FormSelect from 'components/Select/FormSelect';
import { useFetchData } from 'hooks/useFetchData';
import { getDishesShortBySubCategoryIds } from 'backend/dishes';
import ChunkOfTextWithCopying from 'components/Text/ChunkOfTextWithCopying';
import { useTranslation } from 'react-i18next';
import style from './DishNameSelect.module.scss';

interface IDishNameSelect {
	selectedRestaurants: string[];
	selectedTransitionDishSubCategory: Nullable<string>;
	dishTransition: Nullable<ITeaserDishTransition>;
	selectedTransitionDishName: Nullable<string>;
	setTransitionDishName: (value: Nullable<string>) => void;
	setFetchedDishes: (value: IItemForGetShortInfo[]) => void;
}

const DishNameSelect: React.FC<IDishNameSelect> = ({
	selectedRestaurants,
	selectedTransitionDishSubCategory,
	dishTransition,
	selectedTransitionDishName,
	setTransitionDishName,
	setFetchedDishes,
}) => {
	const { response, loading, refetch, responseError } = useFetchData<
		IGetDishesShort,
		IGetDishesShortBySubCategoryIdsArguments
	>(selectedTransitionDishSubCategory ? getDishesShortBySubCategoryIds : null, {
		subCategoryIds: [selectedTransitionDishSubCategory || ''],
		restaurantIds: selectedRestaurants,
	});
	const { t } = useTranslation('error');

	const validationRules = useMemo<Rule[]>(
		() => [
			() => ({
				validator(_, value) {
					if (value && !response?.data.dishes.some((dish) => dish.id === value)) {
						return Promise.reject(new Error(t('someRestaurantDoesNotHaveSuchDish')));
					}
					return Promise.resolve();
				},
			}),
		],
		[t, response],
	);

	const dishesList = useMemo(() => {
		const updatedData = response?.data.dishes.map((el) => ({
			...el,
			additionalContent: (
				<>{el.externalId && <ChunkOfTextWithCopying text={el.externalId} className={style.externalId} />}</>
			),
		}));

		if (!selectedTransitionDishName || updatedData?.some((dish) => dish.id === selectedTransitionDishName)) {
			return updatedData;
		}
		return updatedData
			? [...updatedData, { id: dishTransition?.dish?.id || '', name: dishTransition?.dish?.name || '' }]
			: [];
	}, [response, selectedTransitionDishName]);

	useEffect(() => {
		response && setFetchedDishes(response.data.dishes);
	}, [response]);

	useEffect(() => {
		refetch();
	}, [selectedTransitionDishSubCategory]);

	return (
		<FormSelect
			testId="DishNameSelect"
			name="selectedTransitionDishName"
			label="dishLabel"
			disabled={!selectedTransitionDishSubCategory || loading}
			options={dishesList}
			fetchError={responseError}
			loading={loading}
			onRefetch={refetch}
			onChange={setTransitionDishName}
			validationRules={validationRules}
		/>
	);
};

export default React.memo(DishNameSelect);
